import React, { useState } from 'react';
import { Button, Card, Form, Modal, Table } from 'react-bootstrap';

import { useLanguage } from '../../context/LanguageProvider/LanguageProvider';
import { useAdminData } from '../../context/AdminDataProvider/AdminDataProvider';

const ROLES = ['admin', 'researcher', 'partner', 'viewer'];

const ROLE_BADGE_CLASS = {
  admin: 'badge bg-danger text-white',
  researcher: 'badge bg-primary text-white',
  partner: 'badge bg-success text-white',
  viewer: 'badge bg-secondary text-white',
};

const emptyUser = { name: '', email: '', role: 'researcher', password: '' };

export default function AdminUsersPage() {
  const { t } = useLanguage();
  const { users, addUser, updateUser, deleteUser } = useAdminData();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyUser);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyUser);
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({ name: u.name, email: u.email, role: u.role, password: '' });
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    if (!editingId && !form.password.trim()) return;
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      role: form.role,
      ...(form.password.trim() ? { password: form.password } : {}),
    };
    if (editingId) {
      await updateUser(editingId, payload);
    } else {
      await addUser({ ...payload, password: form.password.trim() });
    }
    closeModal();
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('admin.crud.confirmDelete'))) {
      await deleteUser(id);
    }
  };

  return (
    <div className="jassp-admin-crud">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <h1 className="h3 fw-bold text-dark mb-0">{t('admin.crud.usersTitle')}</h1>
        <Button variant="primary" className="rounded-pill px-4" onClick={openAdd}>
          {t('admin.crud.addUser')}
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th className="ps-4">{t('admin.crud.colName')}</th>
                <th>{t('admin.crud.colEmail')}</th>
                <th>{t('admin.crud.colRole')}</th>
                <th className="text-end pe-4">{t('admin.crud.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="ps-4">{u.name}</td>
                  <td>{u.email}</td>
                  <td>
                    <span className={ROLE_BADGE_CLASS[u.role] || ROLE_BADGE_CLASS.viewer}>
                      {t(`admin.crud.role.${u.role}`)}
                    </span>
                  </td>
                  <td className="text-end pe-4">
                    <Button variant="link" size="sm" className="text-primary p-1" onClick={() => openEdit(u)}>
                      {t('admin.crud.edit')}
                    </Button>
                    <Button variant="link" size="sm" className="text-danger p-1" onClick={() => handleDelete(u.id)}>
                      {t('admin.crud.delete')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={modalOpen} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? t('admin.crud.editUser') : t('admin.crud.addUser')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.crud.colName')}</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.crud.colEmail')}</Form.Label>
              <Form.Control
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{editingId ? t('admin.crud.password') : t('admin.crud.passwordNew')}</Form.Label>
              <Form.Control
                type="password"
                autoComplete={editingId ? 'new-password' : 'new-password'}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                required={!editingId}
              />
              {editingId ? <Form.Text muted>{t('admin.crud.passwordHintEdit')}</Form.Text> : null}
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('admin.crud.colRole')}</Form.Label>
              <Form.Select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t(`admin.crud.role.${r}`)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" type="button" onClick={closeModal}>
              {t('admin.crud.cancel')}
            </Button>
            <Button variant="primary" type="submit">
              {t('admin.crud.save')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
